// src/Pages/ProviderProfile/GoogleLocationDialog.jsx
// OR wherever PageSelectionDialog lives — match that path

import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Box, Radio, RadioGroup,
  FormControlLabel, CircularProgress
} from '@mui/material';
import {
  LocationOn  as LocationIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';
import { useState } from 'react';

const GoogleLocationDialog = ({
  open,
  onClose,
  locations = [],
  sessionId,
  onComplete        // async (sessionId, locationName) => void
}) => {
  const [selected, setSelected] = useState('');
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');

  const handleComplete = async () => {
    if (!selected) return;
    setLoading(true);
    setError('');
    try {
      await onComplete(sessionId, selected);
      // parent handles navigation — no need to reset here
    } catch (e) {
      setError('Failed to connect location. Please try again.');
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography fontWeight={800} sx={{ fontSize: 22, color: '#4285F4' }}>
            G
          </Typography>
          <Typography variant="h6" fontWeight={600}>
            Select Business Location
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Choose which Google Business Profile location to connect to Revfer.
        </Typography>
      </DialogTitle>

      <DialogContent>
        {locations.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No locations found. Please try again.
          </Typography>
        ) : (
          <RadioGroup
            value={selected}
            onChange={e => setSelected(e.target.value)}
          >
            {locations.map(loc => (
              <Box
                key={loc.locationName}
                onClick={() => setSelected(loc.locationName)}
                sx={{
                  p:            2,
                  mb:           1.5,
                  borderRadius: 2,
                  border:       '1px solid',
                  borderColor:  selected === loc.locationName
                    ? 'primary.main'
                    : 'divider',
                  bgcolor:      selected === loc.locationName
                    ? 'primary.50'
                    : 'background.paper',
                  cursor:       'pointer',
                  transition:   'all 0.15s',
                  '&:hover': {
                    borderColor: 'primary.light',
                    bgcolor:     'grey.50'
                  }
                }}
              >
                <FormControlLabel
                  value={loc.locationName}
                  control={<Radio size="small" />}
                  label={
                    <Box sx={{ ml: 0.5 }}>
                      <Typography variant="subtitle2" fontWeight={700}>
                        {loc.title}
                      </Typography>
                      {loc.address && (
                        <Box sx={{
                          display:    'flex',
                          alignItems: 'center',
                          gap:        0.5,
                          mt:         0.25
                        }}>
                          <LocationIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary">
                            {loc.address}
                          </Typography>
                        </Box>
                      )}
                      {loc.phone && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', mt: 0.25 }}
                        >
                          {loc.phone}
                        </Typography>
                      )}
                    </Box>
                  }
                  sx={{ m: 0, width: '100%', alignItems: 'flex-start' }}
                />
              </Box>
            ))}
          </RadioGroup>
        )}

        {error && (
          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
            {error}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button
          onClick={onClose}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleComplete}
          disabled={!selected || loading}
          startIcon={loading
            ? <CircularProgress size={16} color="inherit" />
            : <CheckIcon />}
        >
          {loading ? 'Connecting...' : 'Connect Location'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GoogleLocationDialog;